import { get, writable } from 'svelte/store';
import { type BackendActor, createActor } from './actor';
import {
  AuthClient,
  IdbStorage,
  KEY_STORAGE_DELEGATION,
} from '@dfinity/auth-client';
import { addNotification } from './notifications';
import { DelegationChain } from '@dfinity/identity';

export type AuthenticatedState = {
  state: 'authenticated';
  principal: string;
  actor: BackendActor;
  client: AuthClient;
  bc: BroadcastChannel;
  logout: () => Promise<void>;
};

export type AuthState =
  | {
      state: 'initializing-auth';
    }
  | {
      state: 'anonymous';
      actor: BackendActor;
      client: AuthClient;
    }
  | AuthenticatedState
  | {
      state: 'error';
      error: string;
      client: AuthClient;
    };

export const auth = writable<AuthState>({
  state: 'initializing-auth',
});

async function initAuth() {
  const client = await AuthClient.create({
    idleOptions: {
      disableIdle: true,
      disableDefaultIdleCallback: true,
    },
  });
  if (await client.isAuthenticated()) {
    authenticate(client);
  } else {
    auth.update(() => ({
      state: 'anonymous',
      actor: createActor(),
      client,
    }));
  }
}

initAuth();

export function login() {
  const currentAuth = get(auth);

  if (currentAuth.state === 'anonymous' || currentAuth.state === 'error') {
    currentAuth.client.login({
      maxTimeToLive: BigInt(365 * 24 * 3600) * BigInt(1_000_000_000), // 1 year

      identityProvider: import.meta.env.VITE_INTERNET_IDENTITY_ADDRESS,
      onSuccess: () => authenticate(currentAuth.client),

      onError: (e) => {
        addNotification({
          message: 'Could not sign in with Internet Identity.',
          type: 'error',
        });
        auth.update(() => ({
          state: 'error',
          error: 'Could not sign in with Internet Identity: ' + e,
          client: currentAuth.client,
        }));
      },
    });
  }
}

async function getRemainingSessionTimeMs(): Promise<number | null> {
  const idbStorage: IdbStorage = new IdbStorage();
  const delegationChain: string | null = await idbStorage.get(
    KEY_STORAGE_DELEGATION
  );
  const maybeDelegation =
    delegationChain !== null ? DelegationChain.fromJSON(delegationChain) : null;

  if (maybeDelegation) {
    const maybeExpirationTime: bigint | undefined =
      maybeDelegation.delegations[0]?.delegation.expiration;

    if (maybeExpirationTime) {
      const remainingTimeMs =
        Number(maybeExpirationTime / 1_000_000n) - Date.now();

      return remainingTimeMs;
    }
  }

  return null;
}

export async function logout() {
  const currentAuth = get(auth);

  if (currentAuth.state === 'authenticated') {
    await currentAuth.client.logout();
    currentAuth.bc.postMessage('logout');
    auth.update(() => ({
      state: 'anonymous',
      actor: createActor(),
      client: currentAuth.client,
    }));
  }
}

export async function authenticate(client: AuthClient) {
  // handleSessionTimeout();

  console.log(
    `Remaining session time: ${(await getRemainingSessionTimeMs()) / 1000}s`
  );

  try {
    const actor = createActor({
      agentOptions: {
        identity: client.getIdentity(),
      },
    });

    const logoutBc = new BroadcastChannel('ic_enemy_logout');
    logoutBc.addEventListener('message', (e: MessageEvent<string>) => {
      if (e.data === 'logout') {
        logout();
      }
    });

    auth.update(() => ({
      state: 'authenticated',
      principal: client.getIdentity().getPrincipal().toText(),
      actor,
      client,
      logout,
      bc: logoutBc,
    }));
  } catch (e) {
    auth.update(() => ({
      state: 'error',
      error: e.message || 'An error occurred',
      client,
    }));
  }
}

// // set a timer when the II session will expire and log the user out
// function handleSessionTimeout() {
//   // upon login the localstorage items may not be set, wait for next tick
//   setTimeout(() => {
//     // try {
//     const delegation = JSON.parse(
//       window.localStorage.getItem('ic-delegation')
//     ) as JsonnableDelegationChain;

//     const expirationTimeMs =
//       Number.parseInt(delegation.delegations[0].delegation.expiration, 16) /
//       1000000;

//     setTimeout(() => {
//       logout();
//     }, expirationTimeMs - Date.now());
//     // } catch {
//     //   console.error('Could not handle delegation expiry.');
//     // }
//   });
// }
